import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X, BookOpen, ArrowRight, ListChecks } from 'lucide-react';
import { sitesApi } from '../../api/client';
import { type GettingStartedState, type GettingStartedStepId } from './gettingStarted';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

interface GettingStartedPanelProps {
  isOpen: boolean;
  onClose: () => void;
  state: GettingStartedState;
  onCreateWebsite: () => void;
  celebration?: boolean;
}

export const GettingStartedPanel: React.FC<GettingStartedPanelProps> = ({
  isOpen,
  onClose,
  state,
  onCreateWebsite,
  celebration = false,
}) => {
  const navigate = useNavigate();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const [isResolvingEdit, setIsResolvingEdit] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusClose = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter(el => !el.hasAttribute('disabled') && el.getClientRects().length > 0);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusClose);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const goToEditPage = async () => {
    if (!state.firstSiteId) return;

    if (state.firstPageId) {
      navigate(`/sites/${state.firstSiteId}/pages/${state.firstPageId}/editor`);
      onClose();
      return;
    }

    setIsResolvingEdit(true);
    try {
      const pages = await sitesApi.getPages(state.firstSiteId);
      const pageId = pages[0]?.id;
      if (pageId) {
        navigate(`/sites/${state.firstSiteId}/pages/${pageId}/editor`);
      } else {
        navigate(`/sites/${state.firstSiteId}`);
      }
      onClose();
    } catch {
      navigate(`/sites/${state.firstSiteId}`);
      onClose();
    } finally {
      setIsResolvingEdit(false);
    }
  };

  const handleStepAction = (stepId: GettingStartedStepId) => {
    switch (stepId) {
      case 'create-site':
        onCreateWebsite();
        onClose();
        break;
      case 'open-site':
        if (state.firstSiteId) {
          navigate(`/sites/${state.firstSiteId}`);
          onClose();
        }
        break;
      case 'edit-page':
        void goToEditPage();
        break;
      case 'publish-site':
        if (state.firstSiteId) {
          navigate(`/sites/${state.firstSiteId}?publish=1`);
          onClose();
        }
        break;
      default:
        break;
    }
  };

  const ctaLabel = (stepId: GettingStartedStepId): string | null => {
    if (stepId !== state.nextStepId) return null;
    switch (stepId) {
      case 'create-site':
        return 'Create website';
      case 'open-site':
        return state.firstSiteId ? 'Open site' : null;
      case 'edit-page':
        return state.firstSiteId ? (isResolvingEdit ? 'Opening…' : 'Edit page') : null;
      case 'publish-site':
        return state.firstSiteId ? 'Publish site' : null;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" data-testid="getting-started-panel">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
              <ListChecks size={18} />
            </div>
            <div className="min-w-0">
              <h2 id={titleId} className="text-lg font-semibold text-gray-900">
                {celebration ? "You're live" : 'Get started'}
              </h2>
              <p className="text-sm text-gray-600">
                {celebration
                  ? 'Your site is published. This checklist will close shortly.'
                  : `${state.completedCount} of ${state.steps.length} complete`}
              </p>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            aria-label="Close get started panel"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-5">
            <div
              className="h-full bg-primary-500 transition-all duration-300"
              style={{
                width: `${(state.completedCount / state.steps.length) * 100}%`,
              }}
            />
          </div>

          <ul className="space-y-3">
            {state.steps.map(step => {
              const isNext = step.id === state.nextStepId;
              const label = ctaLabel(step.id);
              return (
                <li
                  key={step.id}
                  data-testid={isNext ? 'getting-started-next-step' : undefined}
                  className={`rounded-lg border px-4 py-3 transition-colors ${
                    isNext
                      ? 'border-primary-300 bg-primary-50/80 shadow-sm'
                      : step.complete
                        ? 'border-gray-100 bg-gray-50/40 opacity-70'
                        : 'border-gray-100 bg-gray-50/50 opacity-60'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 w-6 h-6 shrink-0 rounded-full flex items-center justify-center ${
                        step.complete
                          ? 'bg-primary-500 text-white'
                          : isNext
                            ? 'bg-white border-2 border-primary-500 text-transparent'
                            : 'bg-white border border-gray-300 text-transparent'
                      }`}
                      aria-hidden
                    >
                      <Check size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p
                          className={`text-sm font-medium ${
                            step.complete
                              ? 'text-gray-500 line-through'
                              : isNext
                                ? 'text-gray-900'
                                : 'text-gray-700'
                          }`}
                        >
                          {step.title}
                        </p>
                        {isNext && (
                          <span className="text-[10px] uppercase tracking-wide font-semibold text-primary-700 bg-primary-100 px-1.5 py-0.5 rounded">
                            Next
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                      <Link
                        to={step.helpHref}
                        className="inline-flex items-center text-xs text-primary-600 hover:text-primary-700 mt-1"
                      >
                        Learn more
                      </Link>
                      {label && (
                        <button
                          type="button"
                          onClick={() => handleStepAction(step.id)}
                          disabled={step.id === 'edit-page' && isResolvingEdit}
                          className="btn-primary text-sm inline-flex items-center justify-center space-x-1 mt-3 w-full disabled:opacity-60"
                        >
                          <span>{label}</span>
                          <ArrowRight size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="p-4 border-t border-gray-200">
          <Link
            to="/help"
            className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600"
          >
            <BookOpen size={16} className="mr-2" />
            Browse all Help Guides
          </Link>
        </div>
      </div>
    </div>
  );
};

export default GettingStartedPanel;
